'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }(); // Copyright (c) 2015 - 2017 Uber Technologies, Inc.
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

// IMLEMENTATION NOTES: Why new layers are created on every render
//
// The key here is to understand the declarative / functional
// programming nature of "reactive" applications.
//
// - In a reactive application, the entire "UI tree"
//   is re-rendered every time something in the application changes.
//
// - The UI framework (such as React or deck.gl) then diffs the rendered
//   tree of UI elements (React Elements or deck.gl Layers) against the
//   previously tree and makes optimized changes (to the DOM or to WebGL state).
//
// - Deck.gl layers are not based on React.
//   But it should be possible to wrap deck.gl layers in React components to
//   enable use of JSX.
//
// The deck.gl model that for the app creates a new set of on layers on every
// render.
// Internally, the new layers are efficiently matched against existing layers
// using layer ids.
//
// All calculated state (programs, attributes etc) are stored in a state object
// and this state object is moved forward to the match layer on every render
// cycle.  The new layer ends up with the state of the old layer (and the
// props of the new layer), while the old layer is simply discarded for
// garbage collecion.
//
/* eslint-disable no-try-catch */


var _layer = require('./layer');

var _layer2 = _interopRequireDefault(_layer);

var _utils = require('./utils');

var _assert = require('assert');

var _assert2 = _interopRequireDefault(_assert);

var _drawAndPick = require('./draw-and-pick');

var _viewports = require('./viewports');

var _luma = require('luma.gl');

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

var LOG_PRIORITY_LIFECYCLE = 2;
var LOG_PRIORITY_LIFECYCLE_MINOR = 3;

var LayerManager = function () {
  function LayerManager(_ref) {
    var gl = _ref.gl;

    _classCallCheck(this, LayerManager);

    this.prevLayers = [];
    this.layers = [];
    // Tracks if any layers were drawn last update
    // Needed to ensure that screen is cleared when no layers are shown
    this.screenCleared = false;
    this.oldContext = {};
    this.context = {
      gl: gl,
      uniforms: {},
      viewport: null,
      viewportChanged: true,
      pickingFBO: null,
      lastPickedInfo: {
        index: -1,
        layerId: null
      }
    };
    Object.seal(this.context);
  }

  _createClass(LayerManager, [{
    key: 'setViewport',
    value: function setViewport(viewport) {
      (0, _assert2.default)(viewport instanceof _viewports.Viewport, 'Invalid viewport');

      // TODO - viewport change detection breaks METER_OFFSETS mode
      // const oldViewport = this.context.viewport;
      // const viewportChanged = !oldViewport || !viewport.equals(oldViewport);

      var viewportChanged = true;

      if (viewportChanged) {
        Object.assign(this.oldContext, this.context);
        this.context.viewport = viewport;
        this.context.viewportChanged = true;
        this.context.uniforms = {};
        (0, _utils.log)(4, viewport);
      }

      return this;
    }
  }, {
    key: 'updateLayers',
    value: function updateLayers(_ref2) {
      var newLayers = _ref2.newLayers;

      /* eslint-disable */
      (0, _assert2.default)(this.context.viewport, 'LayerManager.updateLayers: viewport not set');

      // Filter out any null layers
      newLayers = newLayers.filter(function (newLayer) {
        return newLayer !== null;
      });

      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;

      try {
        for (var _iterator = newLayers[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var layer = _step.value;

          layer.context = this.context;
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return) {
            _iterator.return();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }

      this.prevLayers = this.layers;

      var _updateLayers2 = this._updateLayers({
        oldLayers: this.prevLayers,
        newLayers: newLayers
      }),
          error = _updateLayers2.error,
          generatedLayers = _updateLayers2.generatedLayers;

      this.layers = generatedLayers;
      // Throw first error found, if any
      if (error) {
        throw error;
      }
      return this;
    }
  }, {
    key: 'drawLayers',
    value: function drawLayers(_ref3) {
      var pass = _ref3.pass;

      (0, _assert2.default)(this.context.viewport, 'LayerManager.drawLayers: viewport not set');

      (0, _drawAndPick.drawLayers)({ layers: this.layers, pass: pass });

      return this;
    }
  }, {
    key: 'pickLayer',
    value: function pickLayer(_ref4) {
      var x = _ref4.x,
          y = _ref4.y,
          mode = _ref4.mode;
      var _context = this.context,
          gl = _context.gl,
          uniforms = _context.uniforms;

      // Set up a frame buffer if needed

      if (this.context.pickingFBO === null || gl.canvas.width !== this.context.pickingFBO.width || gl.canvas.height !== this.context.pickingFBO.height) {
        this.context.pickingFBO = new _luma.FramebufferObject(gl, {
          width: gl.canvas.width,
          height: gl.canvas.height
        });
      }
      return (0, _drawAndPick.pickLayers)(gl, {
        x: x,
        y: y,
        uniforms: {
          renderPickingBuffer: true,
          picking_uEnable: true
        },
        layers: this.layers,
        mode: mode,
        viewport: this.context.viewport,
        pickingFBO: this.context.pickingFBO,
        lastPickedInfo: this.context.lastPickedInfo
      });
    }
  }, {
    key: 'needsRedraw',
    value: function needsRedraw() {
      var _ref5 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref5$clearRedrawFlag = _ref5.clearRedrawFlags,
          clearRedrawFlags = _ref5$clearRedrawFlag === undefined ? false : _ref5$clearRedrawFlag;

      if (!this.context.viewport) {
        return false;
      }

      var redraw = false;

      // Make sure that buffer is cleared once when layer list becomes empty
      if (this.layers.length === 0) {
        if (this.screenCleared === false) {
          redraw = true;
          this.screenCleared = true;
          return true;
        }
      } else {
        if (this.screenCleared === true) {
          this.screenCleared = false;
        }
      }

      var _iteratorNormalCompletion2 = true;
      var _didIteratorError2 = false;
      var _iteratorError2 = undefined;

      try {
        for (var _iterator2 = this.layers[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
          var layer = _step2.value;

          redraw = redraw || layer.getNeedsRedraw({ clearRedrawFlags: clearRedrawFlags });
        }
      } catch (err) {
        _didIteratorError2 = true;
        _iteratorError2 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion2 && _iterator2.return) {
            _iterator2.return();
          }
        } finally {
          if (_didIteratorError2) {
            throw _iteratorError2;
          }
        }
      }

      return redraw;
    }

    // PRIVATE METHODS

    // Match all layers, checking for caught errors
    // To avoid having an exception in one layer disrupt other layers

  }, {
    key: '_updateLayers',
    value: function _updateLayers(_ref6) {
      var oldLayers = _ref6.oldLayers,
          newLayers = _ref6.newLayers;

      // Create old layer map
      var oldLayerMap = {};
      var _iteratorNormalCompletion3 = true;
      var _didIteratorError3 = false;
      var _iteratorError3 = undefined;

      try {
        for (var _iterator3 = oldLayers[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
          var oldLayer = _step3.value;

          if (oldLayerMap[oldLayer.id]) {
            _utils.log.once(0, 'Multipe old layers with same id ' + layerName(oldLayer));
          } else {
            oldLayerMap[oldLayer.id] = oldLayer;
          }
        }

        // Allocate array for generated layers
      } catch (err) {
        _didIteratorError3 = true;
        _iteratorError3 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion3 && _iterator3.return) {
            _iterator3.return();
          }
        } finally {
          if (_didIteratorError3) {
            throw _iteratorError3;
          }
        }
      }

      var generatedLayers = [];

      // Match sublayers
      var error = this._matchSublayers({
        newLayers: newLayers, oldLayerMap: oldLayerMap, generatedLayers: generatedLayers
      });

      var error2 = this._finalizeOldLayers(oldLayers);
      var firstError = error || error2;
      return { error: firstError, generatedLayers: generatedLayers };
    }

    /* eslint-disable max-statements */

  }, {
    key: '_matchSublayers',
    value: function _matchSublayers(_ref7) {
      var _this = this;

      var newLayers = _ref7.newLayers,
          oldLayerMap = _ref7.oldLayerMap,
          generatedLayers = _ref7.generatedLayers;

      // Filter out any null layers
      newLayers = newLayers.filter(function (newLayer) {
        return newLayer !== null;
      });

      var error = null;
      var _iteratorNormalCompletion4 = true;
      var _didIteratorError4 = false;
      var _iteratorError4 = undefined;

      try {
        var _loop = function _loop() {
          var newLayer = _step4.value;

          newLayer.context = _this.context;

          try {
            // 1. given a new coming layer, find its matching layer
            var oldLayer = oldLayerMap[newLayer.id];
            oldLayerMap[newLayer.id] = null;

            if (oldLayer === null) {
              _utils.log.once(0, 'Multipe new layers with same id ' + layerName(newLayer));
            }

            // Only transfer state at this stage. We must not generate exceptions
            // until all layers' state have been transferred
            if (oldLayer) {
              (0, _utils.log)(LOG_PRIORITY_LIFECYCLE_MINOR, 'matched ' + layerName(newLayer), oldLayer, '=>', newLayer);
              _this._transferLayerState(oldLayer, newLayer);
              _this._updateLayer(newLayer);
            } else {
              _this._initializeNewLayer(newLayer);
            }
            generatedLayers.push(newLayer);

            // Call layer lifecycle method: render sublayers
            var sublayers = newLayer.renderLayers();
            // End layer lifecycle method: render sublayers

            if (sublayers) {
              sublayers = Array.isArray(sublayers) ? sublayers : [sublayers];

              // populate reference to parent layer
              sublayers.forEach(function (layer) {
                layer.parentLayer = newLayer;
              });

              _this._matchSublayers({
                newLayers: sublayers,
                oldLayerMap: oldLayerMap,
                generatedLayers: generatedLayers
              });
            }
          } catch (err) {
            _utils.log.once(0, 'deck.gl error during matching of ' + layerName(newLayer) + ' ' + err, err);
            // Save first error
            error = error || err;
          }
        };

        for (var _iterator4 = newLayers[Symbol.iterator](), _step4; !(_iteratorNormalCompletion4 = (_step4 = _iterator4.next()).done); _iteratorNormalCompletion4 = true) {
          _loop();
        }
      } catch (err) {
        _didIteratorError4 = true;
        _iteratorError4 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion4 && _iterator4.return) {
            _iterator4.return();
          }
        } finally {
          if (_didIteratorError4) {
            throw _iteratorError4;
          }
        }
      }

      return error;
    }
  }, {
    key: '_transferLayerState',
    value: function _transferLayerState(oldLayer, newLayer) {
      var state = oldLayer.state,
          props = oldLayer.props;

      // sanity check

      (0, _assert2.default)(state, 'deck.gl sanity check - Matching layer has no state');
      (0, _assert2.default)(oldLayer !== newLayer, 'deck.gl sanity check - Matching layer is same');

      // Move state
      newLayer.state = state;
      newLayer.lifecycle = 'Matched. State transferred from previous layer';
      state.layer = newLayer;

      // Update model layer reference
      if (state.model) {
        state.model.userData.layer = newLayer;
      }
      // Keep a temporary ref to the old props, for prop comparison
      newLayer.oldProps = props;
      // oldLayer.state = null;
      newLayer.lifecycle = 'Awaiting garbage collection';
    }

    // Update the old layers that were not matched

  }, {
    key: '_finalizeOldLayers',
    value: function _finalizeOldLayers(oldLayers) {
      var error = null;
      // Unmatched layers still have state, it will be discarded
      var _iteratorNormalCompletion5 = true;
      var _didIteratorError5 = false;
      var _iteratorError5 = undefined;

      try {
        for (var _iterator5 = oldLayers[Symbol.iterator](), _step5; !(_iteratorNormalCompletion5 = (_step5 = _iterator5.next()).done); _iteratorNormalCompletion5 = true) {
          var layer = _step5.value;

          if (layer.state) {
            error = error || this._finalizeLayer(layer);
          }
        }
      } catch (err) {
        _didIteratorError5 = true;
        _iteratorError5 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion5 && _iterator5.return) {
            _iterator5.return();
          }
        } finally {
          if (_didIteratorError5) {
            throw _iteratorError5;
          }
        }
      }

      return error;
    }

    // Initializes a single layer, calling layer methods

  }, {
    key: '_initializeNewLayer',
    value: function _initializeNewLayer(layer) {
      var error = null;
      // Check if new layer, and initialize it's state
      if (!layer.state) {
        (0, _utils.log)(LOG_PRIORITY_LIFECYCLE, 'initializing ' + layerName(layer));
        try {
          layer.initializeLayer({
            oldProps: {},
            props: layer.props,
            oldContext: this.oldContext,
            context: this.context,
            changeFlags: layer.diffProps({}, layer.props, this.context)
          });
          layer.lifecycle = 'Intialized';
        } catch (err) {
          _utils.log.once(0, 'deck.gl error during initialization of ' + layerName(layer) + ' ' + err, err);
          // Save first error
          error = error || err;
        }
        // Set back pointer (used in picking)
        if (layer.state) {
          layer.state.layer = layer;
          // Save layer on model for picking purposes
          // TODO - store on model.userData rather than directly on model
        }
        if (layer.state && layer.state.model) {
          layer.state.model.userData.layer = layer;
        }
      }
      return error;
    }

    // Updates a single layer, calling layer methods

  }, {
    key: '_updateLayer',
    value: function _updateLayer(layer) {
      var oldProps = layer.oldProps,
          props = layer.props;

      var error = null;
      if (oldProps) {
        try {
          layer.updateLayer({
            oldProps: oldProps,
            props: props,
            context: this.context,
            oldContext: this.oldContext,
            changeFlags: layer.diffProps(oldProps, layer.props, this.context)
          });
        } catch (err) {
          _utils.log.once(0, 'deck.gl error during update of ' + layerName(layer), err);
          // Save first error
          error = err;
        }
        (0, _utils.log)(LOG_PRIORITY_LIFECYCLE_MINOR, 'updating ' + layerName(layer));
      }
      return error;
    }

    // Finalizes a single layer

  }, {
    key: '_finalizeLayer',
    value: function _finalizeLayer(layer) {
      var error = null;
      var state = layer.state;

      if (state) {
        try {
          layer.finalizeLayer();
        } catch (err) {
          _utils.log.once(0, 'deck.gl error during finalization of ' + layerName(layer), err);
          // Save first error
          error = err;
        }
        // layer.state = null;
        layer.lifecycle = 'Finalized! Awaiting garbage collection';
        (0, _utils.log)(LOG_PRIORITY_LIFECYCLE, 'finalizing ' + layerName(layer));
      }
      return error;
    }
  }]);

  return LayerManager;
}();

exports.default = LayerManager;


function layerName(layer) {
  if (layer instanceof _layer2.default) {
    return '' + layer;
  }
  return !layer ? 'null layer' : 'invalid layer';
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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


//////////////////
// WEBPACK FOOTER
// ./~/deck.gl/dist/lib/layer-manager.js
// module id = 828
// module chunks = 0